import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AdminLayout } from './layouts/AdminLayout';
import { AppLayout } from './layouts/AppLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { PublicLayout } from './layouts/PublicLayout';
import { useSession } from './lib/session';
import type { ReactElement } from 'react';

/**
 * Rutas de 29100 Cloud.
 *
 * Todo lo que no es la primera pantalla va en `lazy`: quien llega a la landing
 * no debería descargar el explorador de archivos ni el panel de plataforma. El
 * router es el único sitio donde se decide qué entra en el bundle inicial, así
 * que la decisión vive aquí y no repartida por los imports.
 */
const LandingPage = lazy(() =>
  import('./pages/public/LandingPage').then((m) => ({ default: m.LandingPage })),
);
const PricingPage = lazy(() =>
  import('./pages/public/PricingPage').then((m) => ({ default: m.PricingPage })),
);
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() =>
  import('./pages/RegisterPage').then((m) => ({ default: m.RegisterPage })),
);
const OnboardingPage = lazy(() =>
  import('./pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const SharePage = lazy(() => import('./pages/SharePage').then((m) => ({ default: m.SharePage })));
const DashboardPage = lazy(() =>
  import('./pages/app/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const FilesPage = lazy(() =>
  import('./pages/app/FilesPage').then((m) => ({ default: m.FilesPage })),
);
const AdminPage = lazy(() => import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })));

const PhotosPage = lazy(() =>
  import('./pages/app/PhotosPage').then((m) => ({ default: m.PhotosPage })),
);
const AlbumsPage = lazy(() =>
  import('./pages/app/AlbumsPage').then((m) => ({ default: m.AlbumsPage })),
);
const AlbumDetailPage = lazy(() =>
  import('./pages/app/AlbumsPage').then((m) => ({ default: m.AlbumDetailPage })),
);
const DocumentRequestsPage = lazy(() =>
  import('./pages/business/DocumentRequestsPage').then((m) => ({
    default: m.DocumentRequestsPage,
  })),
);
const ReviewsPage = lazy(() =>
  import('./pages/business/ReviewsPage').then((m) => ({ default: m.ReviewsPage })),
);
const CleanupPage = lazy(() =>
  import('./pages/app/CleanupPage').then((m) => ({ default: m.CleanupPage })),
);
const DocumentRequestDetailPage = lazy(() =>
  import('./pages/business/DocumentRequestsPage').then((m) => ({
    default: m.DocumentRequestDetailPage,
  })),
);
const DocumentRequestPage = lazy(() =>
  import('./pages/DocumentRequestPage').then((m) => ({ default: m.DocumentRequestPage })),
);
const ChecklistsPage = lazy(() =>
  import('./pages/app/ChecklistsPage').then((m) => ({ default: m.ChecklistsPage })),
);
const ChecklistDetailPage = lazy(() =>
  import('./pages/app/ChecklistsPage').then((m) => ({ default: m.ChecklistDetailPage })),
);
const FavoritesPage = lazy(() =>
  import('./pages/app/FavoritesPage').then((m) => ({ default: m.FavoritesPage })),
);
const TrashPage = lazy(() =>
  import('./pages/app/TrashPage').then((m) => ({ default: m.TrashPage })),
);
const SharedLinksPage = lazy(() =>
  import('./pages/app/SharedLinksPage').then((m) => ({ default: m.SharedLinksPage })),
);
/*
  Las ventanas de elección de «Compartido» y «Espacio». Un solo componente para
  las dos: qué tarjetas enseña sale del catálogo de navegación según la ruta, no
  de una lista escrita dos veces.
*/
const SectionHubPage = lazy(() =>
  import('./pages/app/SectionHubPage').then((m) => ({ default: m.SectionHubPage })),
);
const ActivityPage = lazy(() =>
  import('./pages/app/ActivityPage').then((m) => ({ default: m.ActivityPage })),
);

const SettingsLayout = lazy(() =>
  import('./pages/app/SettingsPage').then((m) => ({ default: m.SettingsLayout })),
);
const ProfileSettings = lazy(() =>
  import('./pages/app/SettingsPage').then((m) => ({ default: m.ProfileSettings })),
);
const SecuritySettings = lazy(() =>
  import('./pages/app/SettingsPage').then((m) => ({ default: m.SecuritySettings })),
);
const StorageSettings = lazy(() =>
  import('./pages/app/SettingsPage').then((m) => ({ default: m.StorageSettings })),
);
const SubscriptionSettings = lazy(() =>
  import('./pages/app/SettingsPage').then((m) => ({ default: m.SubscriptionSettings })),
);
const ReceiptsSettings = lazy(() =>
  import('./pages/app/SettingsPage').then((m) => ({ default: m.ReceiptsSettings })),
);
const PrivacySettings = lazy(() =>
  import('./pages/app/SettingsPage').then((m) => ({ default: m.PrivacySettings })),
);

const TeamPage = lazy(() =>
  import('./pages/business/TeamPage').then((m) => ({ default: m.TeamPage })),
);
const SharedFoldersPage = lazy(() =>
  import('./pages/business/SharedFoldersPage').then((m) => ({ default: m.SharedFoldersPage })),
);
const ReportsPage = lazy(() =>
  import('./pages/business/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);

const AdminOrganizationsPage = lazy(() =>
  import('./pages/admin/AdminOperationPages').then((m) => ({ default: m.AdminOrganizationsPage })),
);
const AdminTenantsPage = lazy(() =>
  import('./pages/admin/AdminTenantsPage').then((m) => ({ default: m.AdminTenantsPage })),
);
const AdminTenantDetailPage = lazy(() =>
  import('./pages/admin/AdminTenantsPage').then((m) => ({ default: m.AdminTenantDetailPage })),
);
const AdminPlansPage = lazy(() =>
  import('./pages/admin/AdminPages').then((m) => ({ default: m.AdminPlansPage })),
);
const AdminStoragePage = lazy(() =>
  import('./pages/admin/AdminOperationPages').then((m) => ({ default: m.AdminStoragePage })),
);
const AdminMarginsPage = lazy(() =>
  import('./pages/admin/AdminOperationPages').then((m) => ({ default: m.AdminMarginsPage })),
);
const AdminReceiptsPage = lazy(() =>
  import('./pages/admin/AdminOperationPages').then((m) => ({ default: m.AdminReceiptsPage })),
);
const AdminAuditPage = lazy(() =>
  import('./pages/admin/AdminOperationPages').then((m) => ({ default: m.AdminAuditPage })),
);

const TermsPage = lazy(() =>
  import('./pages/public/LegalPages').then((m) => ({ default: m.TermsPage })),
);
const PrivacyPage = lazy(() =>
  import('./pages/public/LegalPages').then((m) => ({ default: m.PrivacyPage })),
);
const AcceptableUsePage = lazy(() =>
  import('./pages/public/LegalPages').then((m) => ({ default: m.AcceptableUsePage })),
);
const ContactPage = lazy(() =>
  import('./pages/public/LegalPages').then((m) => ({ default: m.ContactPage })),
);

const ForgotPasswordPage = lazy(() =>
  import('./pages/public/PasswordPages').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./pages/public/PasswordPages').then((m) => ({ default: m.ResetPasswordPage })),
);
const VerifyEmailPage = lazy(() =>
  import('./pages/public/PasswordPages').then((m) => ({ default: m.VerifyEmailPage })),
);

const NotFoundPage = lazy(() =>
  import('./pages/public/ErrorPages').then((m) => ({ default: m.NotFoundPage })),
);
const ForbiddenPage = lazy(() =>
  import('./pages/public/ErrorPages').then((m) => ({ default: m.ForbiddenPage })),
);
const ServerErrorPage = lazy(() =>
  import('./pages/public/ErrorPages').then((m) => ({ default: m.ServerErrorPage })),
);

/**
 * Puerta de las rutas privadas.
 *
 * Recuerda a dónde iba el visitante: sin eso, escribir `/admin` en la barra
 * llevaba al login y, tras entrar, a la portada — el destino se perdía por el
 * camino y parecía que la ruta no existía.
 */
function Protected({ children }: { children: ReactElement }) {
  const { session, loading } = useSession();
  const location = useLocation();

  if (loading)
    return (
      <p className="grid min-h-dvh place-items-center text-muted" role="status" aria-busy="true" />
    );
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

export function App() {
  return (
    <Suspense
      fallback={
        <div className="grid min-h-dvh place-items-center" role="status" aria-busy="true" />
      }
    >
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/acceptable-use" element={<AcceptableUsePage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Route>

        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
        </Route>

        {/* Enlace público: fuera de todo layout, es la URL que se comparte. */}
        <Route path="/s/:token" element={<SharePage />} />
        {/*
          El portal de carga. Va junto a `/s/:token` porque comparte lo esencial
          —sin sesión, sin armazón de aplicación— pero bajo su propio prefijo:
          `/s` es de lectura y `/r` escribe, y esa frontera se ve en la URL.
        */}
        <Route path="/r/:token" element={<DocumentRequestPage />} />

        <Route
          element={
            <Protected>
              <AppLayout />
            </Protected>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/files" element={<FilesPage />} />
          <Route path="/photos" element={<PhotosPage />} />
          <Route path="/albums" element={<AlbumsPage />} />
          <Route path="/albums/:albumId" element={<AlbumDetailPage />} />
          <Route path="/checklists" element={<ChecklistsPage />} />
          <Route path="/checklists/:checklistId" element={<ChecklistDetailPage />} />
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/trash" element={<TrashPage />} />
          <Route path="/shared-links" element={<SharedLinksPage />} />
          <Route path="/shared" element={<SectionHubPage />} />
          <Route path="/space" element={<SectionHubPage />} />
          <Route path="/activity" element={<ActivityPage />} />

          <Route path="/settings" element={<SettingsLayout />}>
            <Route index element={<ProfileSettings />} />
            <Route path="security" element={<SecuritySettings />} />
            <Route path="storage" element={<StorageSettings />} />
            <Route path="subscription" element={<SubscriptionSettings />} />
            <Route path="receipts" element={<ReceiptsSettings />} />
            <Route path="privacy" element={<PrivacySettings />} />
          </Route>

          <Route path="/business/team" element={<TeamPage />} />
          <Route path="/business/shared-folders" element={<SharedFoldersPage />} />
          <Route path="/business/reports" element={<ReportsPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/cleanup" element={<CleanupPage />} />
          <Route path="/business/requests" element={<DocumentRequestsPage />} />
          <Route path="/business/requests/:requestId" element={<DocumentRequestDetailPage />} />
        </Route>

        <Route
          path="/onboarding"
          element={
            <Protected>
              <OnboardingPage />
            </Protected>
          }
        />

        {/*
          El panel de plataforma, con armazón propio.

          Antes `/admin` iba suelto —sin ninguna navegación— y sus subpáginas se
          dibujaban dentro del layout personal, así que administrar te dejaba
          rodeado de «Mis archivos» y del selector de espacio. Son dos trabajos
          distintos y ahora tienen dos armazones distintos.

          La puerta real sigue estando en el backend, que comprueba
          `is_platform_admin` en cada petición; `AdminLayout` sólo evita enseñar
          el panel entero a quien no es operador.
        */}
        <Route
          element={
            <Protected>
              <AdminLayout />
            </Protected>
          }
        >
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/tenants" element={<AdminTenantsPage />} />
          <Route path="/admin/tenants/:workspaceId" element={<AdminTenantDetailPage />} />
          <Route path="/admin/organizations" element={<AdminOrganizationsPage />} />
          <Route path="/admin/plans" element={<AdminPlansPage />} />
          <Route path="/admin/margins" element={<AdminMarginsPage />} />
          <Route path="/admin/receipts" element={<AdminReceiptsPage />} />
          <Route path="/admin/storage" element={<AdminStoragePage />} />
          <Route path="/admin/audit-logs" element={<AdminAuditPage />} />
        </Route>

        <Route path="/403" element={<ForbiddenPage />} />
        <Route path="/500" element={<ServerErrorPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
